import {
  getSocketTransportOptions,
  isEventAcceptableInTime,
  hashCode,
  isConversationStart,
  isClientMessage,
  getTodayDate,
  getDateDayInSpecificTimeZone,
  getNormalizedMessages,
  getMessagesFromConversations
} from '../../lib/utils'
import audioLib from '../../lib/audio'
import videoLib from '../../lib/video'
import { mergeWithStateWebchatSlots } from '../../lib/slot'
import config from '../../config/index'
import conversationHubConfig from '../../config/conversationHub'
import ObjectId from 'bson-objectid'
import { getDeviceDetails } from '../../lib/ua'
import SocketIOFileUpload from 'socketio-file-upload'

import { getItem, setItem } from '../../lib/localStorage'
import { isWindowClosed } from '../../lib/window'
const SOCKET_CONNECT = 'SOCKET_CONNECT'
const SOCKET_DISCONNECT = 'SOCKET_DISCONNECT'
const SOCKET_CONNECT_ERROR = 'SOCKET_CONNECT_ERROR'
const SOCKET_MESSAGE_SEND = 'SOCKET_MESSAGE:SEND'
const SOCKET_MESSAGE_RECEIVED = 'SOCKET_MESSAGE:RECEIVED'
const SOCKET_MESSAGE_SYNC = 'SOCKET_MESSAGE:SYNC'
const SOCKET_USER_MESSAGE = 'SOCKET_USER_MESSAGE'
const SOCKET_CHAT_LOAD = 'SOCKET_CHAT:LOAD'
const SOCKET_CONVERSATIONS_SEND = 'SOCKET_CONVERSATIONS:SEND'
const SOCKET_FILE_SEND = 'SOCKET_FILE:SEND'
const SOCKET_TIMEOUT = 'SOCKET_TIMEOUT'
const SOCKET_SET_WEBCHAT_SLOTS = 'SOCKET_SET:WEBCHAT_SLOTS'
const SET_ASSISTANT_ID = 'SET_ASSISTANT_ID'
const SET_TOKEN = 'SET_TOKEN'
const START_AS_NEW_USER = 'START_AS_NEW_USER'
const ADD_FORMATTED_MESSAGES = 'ADD_FORMATTED_MESSAGES'
const SET_IS_SHOWN_UPLOAD_PROGRESS = 'SET_IS_SHOWN_UPLOAD_PROGRESS'
const SET_FILE_UPLOAD_PROGRESS = 'SET_FILE_UPLOAD_PROGRESS'
const SET_UPLOAD_FILE_NAME = 'SET_UPLOAD_FILE_NAME'
const SET_STT_STATUS = 'SET_STT_STATUS'
const SET_USER_ADMIN = 'SET_USER_ADMIN'
const SET_USER_DETAILS = 'SET_USER_DETAILS'
const SET_IS_DEMO = 'SET_IS_DEMO'
const SET_AUDIO_PLAYING = 'SET_AUDIO_PLAYING'
const SET_SELECTED_HUB_TAB = 'SET_SELECTED_HUB_TAB'

/**
 * Add / Update already extracted conversationContextList values from context, last persistant slots context details
 * @param {Object} context can be lastConversation context or last action context
 * @param {Number} context.nbWelcomes number of time use had conversation with the assistant
 * @param {Array<Object>} context.persistantSlots {localizedNames, slug, slotValue, slotExtractionSource}
 * @param {String} context.persistantSlots.slug slot slug
 * @param {String} context.persistantSlots.slotValue slot value
 * @param {String} context.persistantSlots.slotExtractionSource slot slotExtractionSource, can be boolean
 * @param {Array<Object>} context.persistantSlots.localizedNames list of available slot localized labels [{locale: label}]
 * @param {Array<Object>} conversationContextList already extracted context data from current conversation
 * @returns {Array<Object>} updated conversationContextList
 */
function upsertConversationContextList(context, conversationContextList) {
  // Get nbWelcomes context to show the "Number of visit" of the user
  if (context.hasOwnProperty('nbWelcomes')) {
    const slotContext = conversationContextList.find(
      context => context.slug === config.NB_SESSIONS && !context.localizedNames
    )
    const nbWelcomes = context.nbWelcomes ? context.nbWelcomes : 1

    if (slotContext) {
      slotContext.value = nbWelcomes
    } else {
      conversationContextList.push({
        slug: config.NB_SESSIONS,
        slotValue: nbWelcomes
      })
    }
  }

  if (!context.persistantSlots || !context.persistantSlots.length) {
    return conversationContextList
  }

  // Get other contexts from the persistantSlots
  context.persistantSlots.forEach(persistantSlot => {
    const slotContext = conversationContextList.find(
      context => context.slug === persistantSlot.slug && context.localizedNames
    )

    if (slotContext) {
      slotContext.slotValue = persistantSlot.slotValue
    } else {
      conversationContextList.push(Object.assign({ localizedNames: {} }, persistantSlot))
    }
  })

  return conversationContextList
}

/**
 * Get slots contexts, nbWelcomes from the interations
 * @param {Array<Object>} interations - array of interations
 * @param {String} idOldestConversation
 * @returns {Array} conversationContextList
 */
function getContextFromLastConversation(interactions, idOldestConversation) {
  const conversationContextList = []

  if (!interactions.length) {
    return conversationContextList
  }

  // copy the conversation and reverse the interactions in order to pick the latest one first
  const interactionsCopy = [...interactions]
  interactionsCopy.reverse()

  // Get the last valid conversation context from the latest conversation interaction
  const lastInteractionWithContext = interactionsCopy.find(
    interaction => interaction.conversationId === idOldestConversation && interaction.context
  )

  if (!lastInteractionWithContext) {
    return conversationContextList
  }

  return upsertConversationContextList(lastInteractionWithContext.context, conversationContextList)
}

/**
 * Show the "Load Earlier Conversation" button when hasMoreConversations is true and chat window doesn't have scroll bar
 * @param {boolean} hasMoreConversations
 */
function handleLoadMoreMessageBtn(hasMoreConversations) {
  let showBtn = false

  if (
    hasMoreConversations &&
    document.getElementById('chatList').scrollHeight <= document.getElementById('chatBox').scrollHeight
  ) {
    showBtn = true
  }

  this.dispatch('set_is_show_load_btn', showBtn)
}

/**
 *
 * reconnect the socket connection with
 * @param {string} assistantId
 * @param {string} token user bearer token
 */
function reconnectSocket(assistantId, stateToken) {
  // Updating the Authorization header for socket connection, using the retrived token
  // Note : since the socket-io lib change, we also need to update transports for transportOptions Authorization header to be updated too
  const options = getSocketTransportOptions(
    assistantId,
    getItem(config.TOKEN, JSON.stringify({ [assistantId]: stateToken }))
  )
  // IE check
  if (this._vm.$socket.client.io.opts) {
    this._vm.$socket.client.io.opts.transportOptions = options.transportOptions
    this._vm.$socket.client.io.opts.transports = options.transports
  }
  this._vm.$socket.client.io.engine.transportOptions = options.transportOptions
  this._vm.$socket.client.io.engine.transports = options.transports

  this._vm.$socket.client.connect()
}

/**
 * Set Conversation related parameters
 *
 * @param {Object} message
 * @param {Object} lastMessage
 * @param {String} origin
 * @param {Object} data
 * @returns {Object} message
 */
function setConversationStart(message, lastMessage, origin, data) {
  if (!message) {
    return message
  }

  message.isStartOfConversation = isConversationStart(lastMessage, origin, data)

  if (message.isStartOfConversation && !message.conversationNumber) {
    message.conversationNumber = data.conversationNumber
  }

  return message
}

/**
 * Add Custom CSS from string by creating new style tag in head
 * @param {String} customCSS
 */
function addCustomCSS(customCSS) {
  const head = document.head || document.getElementsByTagName('head')[0]
  const styleId = 'website_chat_custom_css'

  if (head && !document.getElementById(styleId)) {
    const style = document.createElement('style')

    // add the new style sheet
    head.appendChild(style)

    style.type = 'text/css'
    style.id = styleId
    // This is required for IE8 and below.
    if (style.styleSheet) {
      style.styleSheet.cssText = customCSS
    } else {
      style.appendChild(document.createTextNode(customCSS))
    }
  }
}

let connectionTimeout = null
let waitingTimeout = null
let disconnectionTimeout = null
let reloadButtonTimeout = null

/**
 * Assign analysed intent to the latest user message in state.messages Array
 * @param {Object} state
 * @param {Object} intentAnalysed
 */
function assignIntentAnalysedToUserMessage(state, intentAnalysed) {
  if (!state.messages || state.messages.length === 0) {
    return
  }

  // Let's not reverse the state itself !!
  const reversedMessages = Array.from(state.messages)
  reversedMessages.reverse()

  // Find the last client message
  const findMessageIndex = reversedMessages.findIndex(
    message =>
      isClientMessage(message.origin) && (!message.hasOwnProperty(intentAnalysed) || message.intentAnalysed === null)
  )

  // the intentAnalysed should be null and the message should be the last message sent by the user
  if (
    findMessageIndex !== 0 || // Should be last sent message
    !reversedMessages[findMessageIndex] ||
    (reversedMessages[findMessageIndex].hasOwnProperty(intentAnalysed) &&
      reversedMessages[findMessageIndex].intentAnalysed !== null)
  ) {
    return
  }

  // It was reversed index, so correct is messages.length -1

  // Set the data.intentAnalysed to the user message with index found
  state.messages[state.messages.length - 1] = {
    ...state.messages[state.messages.length - 1],
    intentAnalysed: intentAnalysed
  }
}

/**
 * Ensure that the received message is valid and then display it to the user
 * @param {Object} state
 * @param {Object} data received data
 * @param {Boolean} isTTSEnabled Text to speech enabled
 * @param {Boolean} hasMoreConversations
 */
function showNewBotMessage(state, data, isTTSEnabled, hasMoreConversations) {
  /*
   * We should ensure that the message
   * - is displayable (Let's not display missing message props)
   * - not a duplicated welcome message
   */
  if (!data || !data.message) {
    return
  }

  let isMessageAlreadySent = false
  // Find the last client message and check if it's a recent duplicate
  const messageReceivedTime = getItem(`messageReceivedTime${state.assistantId}`)
  const lastMessage = state.messages[state.messages.length - 1]
  const messagesIds = state.messages.map(message => message._id)

  // Compare message id and content of last message
  for (const message of data.message) {
    if (
      messagesIds.includes(message._id) ||
      (lastMessage && message.mainContent && message.mainContent === lastMessage.mainContent)
    ) {
      isMessageAlreadySent = true
    }
  }

  if (
    lastMessage &&
    lastMessage.origin === this._vm.$getConst('ORIGIN_TYPES.SERVER') &&
    isMessageAlreadySent &&
    isEventAcceptableInTime(messageReceivedTime, this._vm.$getConst('SOCKET_RETRY_DELAY'))
  ) {
    return
  }

  // If we have a TTS, play it
  if (data.ttsMP3Url && isTTSEnabled && !isWindowClosed(state.assistantId)) {
    videoLib.pauseVideos()
    audioLib.playMp3(data.ttsMP3Url)
  }

  // If no lastMessage or lastMessage from the client, let's set isStartOfConversation to true
  setConversationStart(lastMessage, lastMessage, this._vm.$getConst('ORIGIN_TYPES.CLIENT'), data)

  const message = {
    message: data.message,
    conversationId: data.conversationId,
    intentAnalysed: null,
    actionDeduced: data.actionDeduced,
    context: data.context,
    isNewMessage: true,
    ...(data.adminId && {
      adminId: data.adminId
    })
  }

  // Set admin avatar for webchat header while an admin is chatting
  if (data.adminId && data.adminId.avatar) {
    this.dispatch('set_live_chat_agent', data.adminId)
  } else {
    this.dispatch('set_live_chat_agent', null)
  }

  if (data.context) {
    const conversationContextList = upsertConversationContextList(data.context, this._vm.conversationContextList)
    this.dispatch('set_conversation_context', conversationContextList)
  }

  // If no lastMessage or lastMessage from the server, let's set isStartOfConversation to true
  const messages = [setConversationStart(message, lastMessage, this._vm.$getConst('ORIGIN_TYPES.SERVER'), data)]

  if (data.conversationNumber !== config.NB_CONVERSATION_THRESHOLD && !hasMoreConversations) {
    this.dispatch('set_is_show_conv_beginning', true)
  }

  const formattedMessages = getNormalizedMessages(messages)
  this.dispatch('set_new_message_count', formattedMessages.length)
  state.messages = state.messages.concat(formattedMessages)
}

/**
 * Hide loading icon and enable the input
 */
function hideLoading() {
  this.dispatch('hide_type_indicator')

  // Enable the user input when new message received
  this.dispatch('set_sending_state', false)

  if (waitingTimeout) {
    clearInterval(waitingTimeout)
    waitingTimeout = null
  }
}

/**
 * Set default live chat agent avatar
 */
function setDefaultLiveChatAgentAvatar() {
  this.dispatch('set_live_chat_agent', {
    avatar: config.DEFAULT_ADMIN_IMAGE
  })
}

/**
 * When the message is for the response of push notification,
 * check the notification style and show the notification text in a bubble if the chat window is closed
 * @param {Object} data
 */
function checkAndPushMinimalNotification(data) {
  if (
    !data.isPushNotification ||
    !data.notificationStyle ||
    data.notificationStyle !== this._vm.$getConst('NOTIFICATION_STYLES.MINIMAL') ||
    !data.message ||
    !data.message.length ||
    this._vm.popinStatus === this._vm.$getConst('POPIN_STATUS.OPENED')
  ) {
    return
  }
  // Get the simple message type message object
  const message = data.message.find(message => this._vm.$getConst('SIMPLE_MESSAGES').includes(message.messageType))
  parent.postMessage(
    { event: config.FRAME_EVENT_TYPES.SENT.ADD_MINIMAL_NOTIFICATION_TEXT, text: message.mainContent },
    '*'
  )
}

let emitTimeout = null

/**
 * Clears the Emit Timeout
 */
function clearEmitTimeout() {
  if (emitTimeout) {
    clearInterval(emitTimeout)
  }

  emitTimeout = null
}

function destroyUploadInstance(commit) {
  if (uploader) {
    uploader.destroy()
  }

  uploader = null
  commit(SET_IS_SHOWN_UPLOAD_PROGRESS, false)
  commit(SET_UPLOAD_FILE_NAME, '')
  this.dispatch('set_input_state', true)
}

/**
 * Get webchat slots from the store
 */
function getWebchatSlots(context) {
  return context.rootGetters.webchatSlots || []
}

/**
 * Populate user messages with common fields
 *
 * @param {Object} context
 * @param {Array<Object>} userMessage
 * @returns {Array<Object>} userMessage
 */
function populateUserMessage(context, origin, userMessage) {
  const webchatSlots = getWebchatSlots(context)

  // Add missing common fields
  for (let index = 0; index < userMessage.length; index++) {
    userMessage[index]._id = ObjectId()
    userMessage[index].origin = origin
    userMessage[index].deviceDetails = getDeviceDetails()
    userMessage[index].locale = context.rootGetters.locale
    userMessage[index].isAdmin = context.rootGetters.user.isAdmin
    userMessage[index].isTTSEnabled = context.rootGetters.isTTSEnabled
    userMessage[index].isDemo = context.rootGetters.isDemo
    userMessage[index].parentUrl = context.rootGetters.parentUrl
    userMessage[index].slots = webchatSlots
  }
  return userMessage
}

/**
 * Add user message to the conversation if not exists(sent from another window)
 * @param {Object} state
 * @param {Object} data
 */
function addUserMessageIfNotExists(state, data) {
  if (!data.userMessage) {
    return
  }

  if (state.messages.some(item => item._id && item._id.toString() === data.userMessage._id.toString())) {
    return
  }

  state.messages.push(data.userMessage)
}

let uploader = null

const mutations = {
  [SOCKET_CONNECT](state) {
    state.connected = true
    // clear the timeout if already set on error
    if (connectionTimeout) {
      clearInterval(connectionTimeout)
      connectionTimeout = null
    }

    if (disconnectionTimeout) {
      clearInterval(disconnectionTimeout)
      disconnectionTimeout = null
    }

    this.dispatch('set_reload_button_state', false)

    if (reloadButtonTimeout) {
      clearTimeout(reloadButtonTimeout)
      reloadButtonTimeout = null
    }
  },

  [SOCKET_DISCONNECT](state) {
    clearInterval(disconnectionTimeout)

    // Wait for 2 seconds before changing the connected status to false
    // to prevent frequent display of server down message
    disconnectionTimeout = setTimeout(() => {
      this.commit(SOCKET_TIMEOUT)
    }, this._vm.$getConst('WAITING_TIME'))

    // When we force new user session, we disconnect the socket, and it should not try to reconnect
    if (state.tryToReconnect) {
      reconnectSocket.bind(this, state.assistantId, state.token)()
    }
  },
  [SOCKET_TIMEOUT](state) {
    state.connected = false

    reloadButtonTimeout = setTimeout(() => {
      this.dispatch('set_reload_button_state', true)
    }, this._vm.$getConst('RELOAD_BUTTON_DELAY'))
  },
  [SOCKET_CONNECT_ERROR](state) {
    // Checking setInterval already called, if not call the reconnectSocket method in interval
    if (!connectionTimeout) {
      connectionTimeout = setInterval(() => {
        reconnectSocket.bind(this, state.assistantId, state.token)()
      }, this._vm.$getConst('WAITING_TIME'))
    }
  },

  [SOCKET_MESSAGE_SEND](state, data) {
    data = data[0] ? data[0] : data

    addUserMessageIfNotExists(state, data)
    assignIntentAnalysedToUserMessage(state, data.intentAnalysed)
    showNewBotMessage.bind(this, state, data, this.state.chat.isTTSEnabled, this.state.chat.hasMoreConversations)()

    this.dispatch('before_new_message')

    // When live chat requested set Live chat agent
    if (data.isLiveChatRequested) {
      setDefaultLiveChatAgentAvatar.bind(this)()
    }

    checkAndPushMinimalNotification.bind(this)(data)

    hideLoading.bind(this)()
  },

  [SOCKET_MESSAGE_RECEIVED]() {
    hideLoading.bind(this)()
  },

  [SOCKET_MESSAGE_SYNC](state, data) {
    this.dispatch('before_new_message')
    addUserMessageIfNotExists(state, data)
  },

  [SOCKET_USER_MESSAGE](state, message) {
    // Don't send user message if it has already been sent and therefore, added to the list of messages
    // Message type Chips does not have _id
    if (state.messages.some(item => item._id && item._id.toString() === message[0]._id.toString())) {
      return
    }
    state.messages.push(message[0])
    this.dispatch('show_type_indicator')

    // Disable the user input until new message receives
    this.dispatch('set_sending_state', true)
    // To move the scrollbar to the new message
    this.dispatch('before_new_message')

    if (waitingTimeout) {
      clearInterval(waitingTimeout)
      waitingTimeout = null
    }

    waitingTimeout = setTimeout(() => {
      this.dispatch('hide_type_indicator')
      this.dispatch('set_sending_state', false)
    }, this._vm.$getConst('MAX_WAITING_TIME'))

    const currentTime = Math.floor(Date.now() / 1000)
    setItem(`messageReceivedTime${state.assistantId}`, currentTime)
  },

  [SOCKET_FILE_SEND](state, data) {
    // Triggered from backend after the file upload complete, so that we can have the s3 url of the file.
    data = data[0] ? data[0] : data
    const message = data.message
    message[0].origin = this._vm.$getConst('ORIGIN_TYPES.CLIENT')
    message[0].deviceDetails = getDeviceDetails()
    message[0].isNewMessage = true

    state.messages.push(message[0])
    // Scroll to bottom of the page
  },

  [SOCKET_CONVERSATIONS_SEND](state, data) {
    data = data[0] ? data[0] : data

    const messages = getMessagesFromConversations(data.conversations)
    const formattedMessages = getNormalizedMessages(messages, true)

    /**
     * If no intermediate conversation to integrate, we add the older conversation to the top of the conversation list
     * And update loader, scroll position, hasMoreConversations, idOldestConversation values
     * So user land on the start of the older conv, and see "load more conversation" button if any
     *
     * If we are receiving an intermediate conversation (conversation that is missing in our conversation list, because it occurred in another tab)
     * We just integrate this missing conversation at the bottom of the existing messages list
     * Scroll position stay the same
     */
    if (!data.isIntermediateConversations) {
      /**
       * Add the older conversation to the top of the conversation list
       * And update loader, scroll, hasMoreConversations, idOldestConversation values
       */
      this.dispatch('set_is_show_loader', false)
      this.dispatch('set_has_more_conversations', data.hasMoreConversations)
      this.dispatch('set_id_oldest_conversation', data.idOldestConversation)

      if (!data.hasMoreConversations) {
        this.dispatch('set_is_show_conv_beginning', true)
      }

      handleLoadMoreMessageBtn.bind(this, data.hasMoreConversations)()
      this.dispatch('addFormattedMessagesToState', formattedMessages.concat(state.messages))
    } else {
      /**
       * Integrate this missing conversation at the bottom of the existing messages list
       */
      state.messages = state.messages.concat(formattedMessages)
    }
  },

  [SOCKET_CHAT_LOAD](state, data) {
    data = data[0] ? data[0] : data
    // Replacing the botTokens cookie with the new token with current bot assistantId
    let tokens = getItem(config.TOKEN, '')
    tokens = tokens ? JSON.parse(tokens) : {}
    const isTokenMatch = state.assistantId in tokens && tokens[state.assistantId] === data.token
    tokens[state.assistantId] = data.token
    this.dispatch('set_token', data.token)
    setItem(config.TOKEN, JSON.stringify(tokens))

    // Disconnect the socket if token does not match or not present and reconnect with the auth headers
    if (!isTokenMatch) {
      this._vm.$socket.client.io.close()
    }

    this.dispatch('set_title', data.title)
    this.dispatch('set_has_more_conversations', data.hasMoreConversations)
    this.dispatch('set_id_oldest_conversation', data.idOldestConversation)
    this.dispatch('set_end_conversation_actions', data.endConversationActions)
    this.dispatch('set_localized_acknowledgement_texts', data.localizedAcknowledgementTexts)
    this.dispatch('set_default_display_delay_rating_panel', data.defaultDisplayDelayRatingPanel)
    this.dispatch('set_user_details', data.user)
    this.dispatch('set_nlp_word_rules', data.nlpWordRules)
    this.dispatch('set_time_zone', data.timeZone)
    this.dispatch('set_conversation_hub', data.conversationHub)
    this.dispatch('set_conversations', data.conversationList)
    this.dispatch('set_post_message_slots_properties', data.postMessageSlots || null)

    let code = ''

    if (data.slug) {
      code = hashCode(data.slug)
    }

    const yeldaPush = this.getters.yeldaPush

    const today = getTodayDate(data.timeZone)

    if (data.pushNotifications) {
      const activePushNotifications = []
      // Push notification can be activated, deactivated, or only only testable by admins (sandbox mode).
      // In that case, the admin need to add the param yelda_push=xxx to the url, with xxx being a hash code of the assistant slug
      // Sandboxed pushNotification should be removed from available push notifications if the correct yelda_push code is not provided
      // Check for pushNotification.locales have the webchat locale
      data.pushNotifications.forEach(pushNotification => {
        if (
          (pushNotification.activationStatus !== config.PUSH_NOTIFICATION_STATUS.ONLY_FOR_ADMINS ||
            (yeldaPush && yeldaPush !== '' && code === yeldaPush)) &&
          pushNotification.locales.indexOf(this.getters.locale) > -1
        ) {
          activePushNotifications.push(pushNotification)
        }
      })

      let pushNotifications = []

      if (data.receivedPushNotifications && data.receivedPushNotifications.length && activePushNotifications.length) {
        activePushNotifications.forEach(pushNotification => {
          // Check if the notification already received
          const receivedNotification = data.receivedPushNotifications.find(receivedNotification => {
            return receivedNotification.pushNotificationId === pushNotification._id
          })

          // If the push notification is in not repeat mode delete it
          // or if the push notification is in repeat mode and already received today delete it
          if (
            !receivedNotification ||
            (receivedNotification &&
              !pushNotification.isSentOneTime &&
              getDateDayInSpecificTimeZone(receivedNotification.dateDay) !== today)
          ) {
            pushNotifications.push(pushNotification)
          }
        })
      } else {
        pushNotifications = activePushNotifications
      }

      this.dispatch('set_push_notifications', pushNotifications)
    }

    if (!data.hasMoreConversations) {
      this.dispatch('set_is_show_conv_beginning', true)
    }

    // Get the conversationNumber from the conversations list
    const conversationNumber =
      (data.conversations && data.conversations.length && data.conversations[0].conversationNumber) || 0

    // Add the bubble text only for fresh conversation
    if (conversationNumber === 0) {
      parent.postMessage(config.FRAME_EVENT_TYPES.SENT.ADD_BUBBLE_TEXT, '*')
    }

    if (conversationNumber <= config.NB_CONVERSATION_THRESHOLD) {
      this.dispatch('set_is_fresh_conversation', true)
      this.dispatch('set_is_show_conv_beginning', false)
    }

    if (data.avatar) {
      this.dispatch('set_avatar', data.avatar)
    }

    if (data.style) {
      this.dispatch('set_style', data.style)
    }

    if (data.voiceOptions) {
      this.dispatch('set_voice_options', data.voiceOptions)
    }

    // upload settings is an assistant settings object which is composed of { {Boolean}: isActivated }
    if (data.uploadSettings) {
      // set_upload_settings will set isFileUploadActivated state value, which will enable or disable file upload
      this.dispatch('set_upload_settings', data.uploadSettings)
    }

    // Custom CSS provided in the website-chat platform settings by admin
    if (data.customCSS && data.customCSS !== '') {
      addCustomCSS(data.customCSS)
    }

    if ('areAudioOptionsVisible' in data) {
      this.dispatch('set_audio_option', data.areAudioOptionsVisible)
    }

    if (data.actionsIgnoredForRatings) {
      this.dispatch('set_actions_ignored_for_ratings', data.actionsIgnoredForRatings)
    }

    if (data.shortcutMenu && data.shortcutMenu.length) {
      this.dispatch('set_shortcut_menu', data.shortcutMenu)
    }

    handleLoadMoreMessageBtn.bind(this, data.hasMoreConversations)()
    let isLiveChatRequested = false
    if ('conversations' in data && data.conversations.length) {
      const messages = getMessagesFromConversations(data.conversations)
      const formattedMessages = getNormalizedMessages(messages, true)
      isLiveChatRequested = (messages[0] && messages[0].isLiveChatRequested) || false
      const conversationContextList = getContextFromLastConversation(data.conversations, data.idOldestConversation)
      this.dispatch('set_conversation_context', conversationContextList)
      this.dispatch('addFormattedMessagesToState', formattedMessages.concat(state.messages))

      // Check maxConversationInactivityDuration with the last message sent time, if it is greater
      // then set open chat on load to false
      if (data.maxConversationInactivityDuration && data.currentTime) {
        const lastConversationTime = new Date(data.conversations[data.conversations.length - 1].sentTime).getTime()
        const diff = new Date(data.currentTime).getTime() - lastConversationTime

        if (diff > data.maxConversationInactivityDuration) {
          this.dispatch('set_popin_status', this._vm.$getConst('POPIN_STATUS.CLOSED'))
          setItem(`popin_status_${state.assistantId}`, this._vm.$getConst('POPIN_STATUS.CLOSED'))
        }
      }
    }

    // When conversation is active and live chat is requested then set live chat agent
    if (data.isConversationActive && isLiveChatRequested) {
      setDefaultLiveChatAgentAvatar.bind(this)()
    }

    state.isChatLoaded = true
  },

  [SOCKET_SET_WEBCHAT_SLOTS](state, { webchatSlots }) {
    if (!webchatSlots || typeof webchatSlots !== 'object') {
      return
    }

    const updatedSlots = mergeWithStateWebchatSlots(state.webchatSlots || [], webchatSlots)

    this.dispatch('set_webchat_slots', updatedSlots)
  },

  [SET_ASSISTANT_ID](state, value) {
    state.assistantId = value
  },
  [SET_TOKEN](state, value) {
    state.token = value
  },
  [START_AS_NEW_USER](state) {
    // Clear the current assistantId's botTokens localstorage
    let tokens = getItem(config.TOKEN, '')
    state.token = ''
    tokens = tokens ? JSON.parse(tokens) : {}
    delete tokens[state.assistantId]
    setItem(config.TOKEN, JSON.stringify(tokens))

    /**
     * Disconnect the socket manually, or else while location reload the socket_disconnect event triggers
     * the socket reconnect method results in not getting any message after reload
     */
    state.tryToReconnect = false
    this._vm.$socket.client.io.close()
    this._vm.$socket.client.io.disconnect()
    location.reload()
  },
  [ADD_FORMATTED_MESSAGES](state, value) {
    // Set previous scroll height to properly scroll once messages are loaded into the webchat
    this.dispatch('set_previous_scroll_height')
    state.messages = value

    // Scroll to last message once the message are added
    setTimeout(() => {
      this.dispatch('scroll_chat_height')
    }, 100)
  },
  [SET_IS_SHOWN_UPLOAD_PROGRESS](state, value) {
    state.isShownUploadProgress = value
  },
  /**
   * Set the file upload progress in percentage which can be assigned as width for progress bar
   * @param {Object} state
   * @param {Number} value
   */
  [SET_FILE_UPLOAD_PROGRESS](state, value) {
    state.fileUploadProgress = value
  },
  /**
   * Set the file name value to the state value so that it can be shown while file uploading
   * @param {Object} state
   * @param {String} value
   */
  [SET_UPLOAD_FILE_NAME](state, value) {
    state.uploadFileName = value
  },
  [SET_STT_STATUS](state, value) {
    state.isSTTBusy = value
  },
  [SET_USER_DETAILS](state, { firstName, lastName }) {
    state.user.firstName = firstName
    state.user.lastName = lastName
  },
  [SET_USER_ADMIN](state, value) {
    state.user.isAdmin = value
  },
  [SET_IS_DEMO](state, value) {
    state.isDemo = value
  },
  [SET_AUDIO_PLAYING](state, value) {
    state.isAudioPlaying = value
  },
  [SET_SELECTED_HUB_TAB](state, value) {
    state.selectedHubTab = value
  }
}

const actions = {
  /**
   * When a message is sent by the user
   *  - Wait for response
   *
   * @param {Object} context - Vuex context
   * @param {Array<Object>} userMessage - user messages
   * @param {String} socketEvent - Socket event to fire
   * @return {Promise}
   */
  socket_userMessage(context, userMessage, socketEvent = 'message:send') {
    userMessage = populateUserMessage(context, this._vm.$getConst('ORIGIN_TYPES.CLIENT'), userMessage)
    // If the user has connection issue the same message can be sent with only a few milliseconds apart
    // These duplicates can be difficult to catch
    // So let's add a last check to see if a message hasn't been sent
    if (context.rootGetters.isSendingMessage) {
      return
    }

    return emitSocketEvent
      .bind(this, socketEvent, context, userMessage)()
      .then(() => {
        return new Promise(resolve => {
          context.commit(SOCKET_USER_MESSAGE, userMessage)
          resolve()
        })
      })
  },

  socket_userTracking(context, data) {
    data = populateUserMessage(context, this._vm.$getConst('ORIGIN_TYPES.CLIENT'), data)
    return emitSocketEvent.bind(this, 'tracking:send', context, data)()
  },

  /**
   * Send the push notification trigger to the socket server
   * @param {Object} context - Vuex context
   * @param {Object} pushNotification
   * @param {String} pushNotification._id
   * @param {String} pushNotification.name
   * @param {String} pushNotification.actionId
   * @param {String} socketEvent - Socket event to fire
   * @return {Promise}
   */
  socket_chatPush(context, pushNotification, socketEvent = 'chat:push') {
    const slots = getWebchatSlots(context)
    return emitSocketEvent.bind(this, socketEvent, context, {
      pushNotification: pushNotification,
      isTTSEnabled: context.rootGetters.isTTSEnabled,
      slots
    })() // promise
  },

  /**
   * Create the user if it does not exist and get the token
   * @param {Object} context - Vuex context
   * @return {Promise}
   */
  socket_chatLoad(context) {
    const slots = getWebchatSlots(context)
    return emitSocketEvent.bind(this, 'chat:load', context, {
      locale: context.rootGetters.locale,
      isAdmin: context.rootGetters.user.isAdmin,
      slots
    })()
  },

  /**
   * Event fired when the chat is opened for the first time
   *
   * @param {Object} context - Vuex context
   * @param {Boolean} isReset - if true, it will force a new conversation to restart in backend
   * @param {String} lastWebchatConversationId
   * @return {Promise}
   */
  socket_chatOpen(context, { isReset = false, lastWebchatConversationId = null } = {}) {
    // reset the conversation context to empty array when starting the new conversation
    if (isReset) {
      this.dispatch('set_conversation_context', [])
    }

    /*
      We pick the conversationId from the last message and send it as lastWebchatConversationId
      so backend can check if any intermediate conversation was generated on different tabs,
      and send back to webchat the missing conversation
    */

    const slots = getWebchatSlots(context)
    return emitSocketEvent.bind(this, 'chat:open', context, {
      parentUrl: context.rootGetters.parentUrl,
      isTTSEnabled: context.rootGetters.isTTSEnabled,
      locale: context.rootGetters.locale,
      isAdmin: context.rootGetters.user.isAdmin,
      reset: isReset,
      lastWebchatConversationId,
      slots
    })()
  },

  /**
   * Load old conversations
   *
   * @param {Object} context - Vuex context
   * @param {Boolean} idOldestConversation - If it's the oldest conversation
   * @return {Promise}
   */
  socket_conversationGet(context, idOldestConversation) {
    this.dispatch('set_is_show_loader', true)
    this.dispatch('set_is_show_load_btn', false)

    return emitSocketEvent.bind(this, 'conversations:get', context, {
      idOldestConversation: idOldestConversation,
      locale: context.rootGetters.locale
    })()
  },

  /**
   * Send user rating
   *
   * @param {Object} context - Vuex context
   * @param {Object} rating - User rating of the conversation
   * @return {Promise}
   */
  socket_userRating(context, rating) {
    return emitSocketEvent.bind(this, 'rating:send', context, rating)()
  },

  /**
   * Send user geo location
   *
   * @param {Object} context - Vuex context
   * @param {Object} geoLocationData - {latitude<Float>, longitude<Float>} User latitude and longitude
   * @return {Promise}
   */
  socket_locationSend(context, geoLocationData) {
    let userMessage = [
      {
        messageType: 'message',
        mainContent: geoLocationData.message,
        inputType: config.INPUT_TYPES.TOUCH
      }
    ]
    userMessage = populateUserMessage(context, this._vm.$getConst('ORIGIN_TYPES.CLIENT'), userMessage)
    const [enrichedGeoLocationData] = populateUserMessage(context, this._vm.$getConst('ORIGIN_TYPES.CLIENT'), [
      geoLocationData
    ])
    return emitSocketEvent
      .bind(this, 'location:send', context, enrichedGeoLocationData)()
      .then(() => {
        return new Promise(resolve => {
          context.commit(SOCKET_USER_MESSAGE, userMessage)
          resolve()
        })
      })
  },
  /**
   * Send user file
   *
   * @param {Object} context - Vuex context
   * @param {File} file
   * @return {Promise}
   */
  socket_fileUpload(context, { file, isDisplayedProgressBar }) {
    uploader = new SocketIOFileUpload(this._vm.$socket.client, {
      topicName: 'file_upload'
    })

    context.commit(SET_UPLOAD_FILE_NAME, file.name)

    // Display Progress for large files (size greater than 500KB)
    if (isDisplayedProgressBar) {
      context.commit(SET_IS_SHOWN_UPLOAD_PROGRESS, true)
      uploader.addEventListener('progress', event => {
        context.commit(SET_FILE_UPLOAD_PROGRESS, Math.round((event.bytesLoaded / event.file.size) * 100))
      })
    }

    uploader.addEventListener('complete', event => {
      destroyUploadInstance.bind(this, context.commit)()
      // add user details like message _id, isDemo, locale, ect
      const [userUpload] = populateUserMessage(context, this._vm.$getConst('ORIGIN_TYPES.CLIENT'), [
        {
          pathName: event.detail.pathName,
          name: event.detail.name
        }
      ])
      return emitSocketEvent.bind(this, 'upload:complete', context, userUpload)()
    })

    uploader.addEventListener('error', event => {
      destroyUploadInstance.bind(this, context.commit)()
    })

    uploader.submitFiles([file])
  },

  abort_fileUpload({ commit }) {
    destroyUploadInstance.bind(this, commit)()
  },

  /**
   * Set the value of assistantId
   *
   * @param {Function} commit - Vuex commit function
   * @param {Boolean} value - New value
   */
  set_assistant_id({ commit }, value) {
    commit(SET_ASSISTANT_ID, value)
  },

  /**
   * Set the value of user bearer token
   *
   * @param {Function} commit - Vuex commit function
   * @param {Boolean} value - New value
   */
  set_token({ commit }, value) {
    commit(SET_TOKEN, value)
  },

  /**
   * Clear the current assistant's saved token to start as a new user
   * @param {Function} commit - Vuex commit function
   */
  start_as_new_user({ commit }) {
    commit(START_AS_NEW_USER)
  },

  addFormattedMessagesToState({ commit }, value) {
    commit(ADD_FORMATTED_MESSAGES, value)
  },

  set_STT_status({ commit }, value) {
    commit(SET_STT_STATUS, value)
  },

  set_user_details({ commit }, details) {
    commit(SET_USER_DETAILS, details)
  },

  set_user_admin({ commit }, status) {
    commit(SET_USER_ADMIN, status)
  },

  set_is_demo({ commit }, value) {
    commit(SET_IS_DEMO, value)
  },

  set_audio_playing({ commit }, value) {
    commit(SET_AUDIO_PLAYING, value)
  },

  set_selected_hub_tab({ commit }, value) {
    commit(SET_SELECTED_HUB_TAB, value)
  }
}

/**
 * Function to send event via socket
 *  - Automatically retry for X times every X time
 *
 * @param {String} event - Event name
 * @param {Object} data - Data of the event
 * @param {Boolean} retry - If this is a retry
 */
const emitSocketEvent = function (event, context, data = {}, retry = 0) {
  // /!\ Make sure to always resolve, or the .then will never be called
  return new Promise(resolve => {
    // Increment the fails number
    if (retry > 0) {
      // Stop here if there is too much fails
      if (retry >= this._vm.$getConst('SOCKET_EMIT_MAX_TRY')) {
        clearEmitTimeout()
        return resolve()
      }
    }

    // Try to emit the event
    // But not point to send it if the chat is not connected
    if (context.rootGetters.connected) {
      this._vm.$socket.client.emit(event, data, () => {
        clearEmitTimeout()
        return resolve()
      })
    }
    // Add a retry if the call fail
    clearEmitTimeout()

    // Let's add a longer delay for welcome : creating a new user and conversation is a bit longer
    const delay = event === 'chat:open' ? 'SOCKET_WELCOME_RETRY_DELAY' : 'SOCKET_RETRY_DELAY'

    emitTimeout = setTimeout(() => {
      // don't count the retry if the webchat was not connected
      if (context.rootGetters.connected) {
        retry += 1
      }
      return resolve(emitSocketEvent.bind(this, event, context, data, retry)())
    }, this._vm.$getConst(delay))
  })
}

const getters = {
  messages: state => state.messages,
  connected: state => state.connected,
  assistantId: state => state.assistantId,
  token: state => state.token,
  isChatLoaded: state => state.isChatLoaded,
  isShownUploadProgress: state => state.isShownUploadProgress,
  fileUploadProgress: state => state.fileUploadProgress,
  uploadFileName: state => state.uploadFileName,
  isSTTBusy: state => state.isSTTBusy,
  user: state => state.user,
  isDemo: state => state.isDemo,
  isAudioPlaying: state => state.isAudioPlaying,
  selectedHubTab: state => state.selectedHubTab
}

export default {
  state: {
    messages: [],
    connected: false,
    assistantId: '',
    token: '',
    isChatLoaded: false,
    user: {},
    tryToReconnect: true,
    isShownUploadProgress: false,
    fileUploadProgress: 0,
    uploadFileName: '',
    isSTTBusy: false,
    isDemo: false,
    isAudioPlaying: false,
    selectedHubTab: conversationHubConfig.TABS.INFORMATION
  },
  getters: getters,
  mutations: mutations,
  actions: actions
}
